// ========================================
// UNIFIED ORDER API SERVICE V3
// Frontend API service for unified order management
// Clean interface to V3 backend endpoints
// ========================================

import axios from 'axios';
import {
  OrderHeader,
  OrderPosition,
  UnifiedOrder,
  CreateOrderRequest,
  UpdateOrderRequest,
  DeliveryRequest,
  SendOrderRequest,
  ApiResponse,
  Supplier,
  DeliveryLog
} from '../types/unifiedOrderTypes';

// API Base URL - configurable for different environments
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api/v3';

class UnifiedOrderService {
  private readonly baseUrl = API_BASE_URL;
  private readonly ordersUrl = `${this.baseUrl}/orders`;
  private readonly suppliersUrl = `${this.baseUrl}/suppliers`;

  // ========================================
  // ERROR HANDLING
  // ========================================

  /**
   * Handle API errors consistently
   */
  private handleError(error: any): Error {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }
    if (error.message) {
      return new Error(error.message);
    }
    return new Error('An unexpected error occurred');
  }

  // ========================================
  // ORDER CRUD OPERATIONS
  // ========================================

  /**
   * Create a new order (any type)
   */
  async createOrder(orderData: CreateOrderRequest): Promise<ApiResponse<{ orderId: string }>> {
    try {
      const response = await axios.post<ApiResponse<{ orderId: string }>>(this.ordersUrl, orderData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all orders with optional filtering
   */
  async getOrders(filters?: {
    order_type?: 'material' | 'heat_treatment' | 'coating';
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<OrderHeader[]>> {
    try {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            params.append(key, value.toString());
          }
        });
      }
      
      const url = params.toString() ? `${this.ordersUrl}?${params}` : this.ordersUrl;
      const response = await axios.get<ApiResponse<OrderHeader[]>>(url);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get a specific order by ID with all positions
   */
  async getOrder(orderId: string): Promise<ApiResponse<UnifiedOrder>> {
    try {
      const response = await axios.get<ApiResponse<UnifiedOrder>>(`${this.ordersUrl}/${orderId}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Update an order
   */
  async updateOrder(orderId: string, updateData: UpdateOrderRequest): Promise<ApiResponse<OrderHeader>> {
    try {
      const response = await axios.put<ApiResponse<OrderHeader>>(`${this.ordersUrl}/${orderId}`, updateData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete an order
   */
  async deleteOrder(orderId: string): Promise<ApiResponse<void>> {
    try {
      const response = await axios.delete<ApiResponse<void>>(`${this.ordersUrl}/${orderId}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // ========================================
  // ORDER WORKFLOW OPERATIONS
  // ========================================

  /**
   * Finalize an order (generate PDF and mark as ready)
   */
  async finalizeOrder(orderId: string, language: 'de' | 'en' = 'de'): Promise<ApiResponse<{ pdfPath: string }>> {
    try {
      const response = await axios.post<ApiResponse<{ pdfPath: string }>>(
        `${this.ordersUrl}/${orderId}/finalize`,
        { language }
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Regenerate PDF for an order
   */
  async regeneratePDF(orderId: string, language: 'de' | 'en' = 'de'): Promise<ApiResponse<{ pdfPath: string }>> {
    try {
      const response = await axios.post<ApiResponse<{ pdfPath: string }>>(
        `${this.ordersUrl}/${orderId}/regenerate-pdf`,
        { language }
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Send an order via email
   */
  async sendOrder(orderId: string, sendData: SendOrderRequest): Promise<ApiResponse<void>> {
    try {
      const response = await axios.post<ApiResponse<void>>(`${this.ordersUrl}/${orderId}/send`, sendData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // ========================================
  // DELIVERY MANAGEMENT
  // ========================================

  /**
   * Record partial delivery for a specific position
   */
  async recordPartialDelivery(
    orderId: string, 
    positionId: string, 
    deliveryData: {
      delivered_quantity: number;
      delivery_date?: string;
      received_by?: string;
      delivery_notes?: string;
    }
  ): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post<ApiResponse<any>>(
        `${this.ordersUrl}/${orderId}/positions/${positionId}/deliver`, 
        deliveryData
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Record bulk delivery for all remaining positions
   */
  async recordBulkDelivery(
    orderId: string, 
    deliveryData: {
      delivery_date?: string;
      received_by?: string;
      delivery_notes?: string;
    }
  ): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post<ApiResponse<any>>(
        `${this.ordersUrl}/${orderId}/deliver`, 
        deliveryData
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get delivery history for an order
   */
  async getDeliveryHistory(orderId: string): Promise<ApiResponse<DeliveryLog[]>> {
    try {
      const response = await axios.get<ApiResponse<DeliveryLog[]>>(
        `${this.ordersUrl}/${orderId}/deliveries`
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Legacy method - use recordPartialDelivery instead
   */
  async recordDelivery(orderId: string, deliveryData: DeliveryRequest): Promise<ApiResponse<OrderPosition>> {
    try {
      const response = await axios.post<ApiResponse<OrderPosition>>(`${this.ordersUrl}/${orderId}/deliveries`, deliveryData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // ========================================
  // FILE OPERATIONS
  // ========================================

  /**
   * Upload a delivery note
   */
  async uploadDeliveryNote(orderId: string, file: File): Promise<ApiResponse<any>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post<ApiResponse<any>>(
        `${this.ordersUrl}/${orderId}/delivery-note`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Upload an invoice
   */
  async uploadInvoice(orderId: string, file: File): Promise<ApiResponse<any>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post<ApiResponse<any>>(
        `${this.ordersUrl}/${orderId}/invoice`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Upload an extra file
   */
  async uploadExtraFile(orderId: string, file: File): Promise<ApiResponse<any>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post<ApiResponse<any>>(
        `${this.ordersUrl}/${orderId}/extra-file`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Download delivery note
   */
  async downloadDeliveryNote(orderId: string): Promise<void> {
    try {
      const response = await axios.get(`${this.ordersUrl}/${orderId}/delivery-note`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `delivery-note-${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Download invoice
   */
  async downloadInvoice(orderId: string): Promise<void> {
    try {
      const response = await axios.get(`${this.ordersUrl}/${orderId}/invoice`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Download extra file
   */
  async downloadExtraFile(orderId: string): Promise<void> {
    try {
      const response = await axios.get(`${this.ordersUrl}/${orderId}/extra-file`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `extra-file-${orderId}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get URL for downloading files (corrected to use v3 endpoint)
   */
  getFileUrl(filePath: string): string {
    // Remove 'storage/' prefix if present since the backend endpoint already resolves to storage directory
    const cleanPath = filePath.startsWith('storage/') ? filePath.substring('storage/'.length) : filePath;
    return `${this.baseUrl}/files/${cleanPath}`;
  }

  /**
   * Get position file download URL
   */
  getPositionFileUrl(orderId: string, positionId: string, fileId: string): string {
    return `${this.baseUrl}/orders/${orderId}/positions/${positionId}/files/${fileId}/download`;
  }

  /**
   * Get PDF download URL
   */
  getPdfUrl(orderId: string): string {
    return `${this.ordersUrl}/${orderId}/pdf`;
  }

  // ========================================
  // SUPPLIERS
  // ========================================

  /**
   * Get all suppliers
   */
  async getSuppliers(): Promise<ApiResponse<Supplier[]>> {
    try {
      const response = await axios.get<ApiResponse<Supplier[]>>(this.suppliersUrl);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // ========================================
  // POSITION MANAGEMENT
  // ========================================

  /**
   * Get all positions for a specific order
   */
  async getOrderPositions(orderId: string): Promise<ApiResponse<OrderPosition[]>> {
    try {
      const response = await axios.get<ApiResponse<OrderPosition[]>>(`${this.ordersUrl}/${orderId}/positions`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Update an order position
   */
  async updateOrderPosition(
    orderId: string, 
    positionId: string, 
    updateData: {
      quantity?: number;
      part_name?: string;
      part_number?: string;
      notes?: string;
      type_specific_data?: any;
      unit_price?: number;
      total_price?: number;
      currency?: string;
    }
  ): Promise<ApiResponse<OrderPosition>> {
    try {
      const response = await axios.put<ApiResponse<OrderPosition>>(
        `${this.ordersUrl}/${orderId}/positions/${positionId}`,
        updateData
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Add a new position to an order
   */
  async addOrderPosition(
    orderId: string, 
    positionData: {
      position_number: number;
      quantity: number;
      part_name?: string;
      part_number?: string;
      notes?: string;
      type_specific_data: any;
    }
  ): Promise<ApiResponse<OrderPosition>> {
    try {
      const response = await axios.post<ApiResponse<OrderPosition>>(
        `${this.ordersUrl}/${orderId}/positions`,
        positionData
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete an order position
   */
  async deleteOrderPosition(orderId: string, positionId: string): Promise<ApiResponse<void>> {
    try {
      const response = await axios.delete<ApiResponse<void>>(
        `${this.ordersUrl}/${orderId}/positions/${positionId}`
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // ========================================
  // STATUS WORKFLOW METHODS
  // ========================================

  /**
   * Change order status
   */
  async changeOrderStatus(orderId: string, statusChange: {
    to_status: string;
    changed_by: string;
    change_reason?: string;
    change_notes?: string;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post<ApiResponse<any>>(
        `${this.ordersUrl}/${orderId}/status`,
        statusChange
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get status change history for an order
   */
  async getStatusHistory(orderId: string): Promise<ApiResponse<any[]>> {
    try {
      const response = await axios.get<ApiResponse<any[]>>(
        `${this.ordersUrl}/${orderId}/status/history`
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get available status transitions for an order
   */
  async getStatusTransitions(orderId: string): Promise<ApiResponse<any>> {
    try {
      const response = await axios.get<ApiResponse<any>>(
        `${this.ordersUrl}/${orderId}/status/transitions`
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get status workflow definition
   */
  async getStatusWorkflow(): Promise<ApiResponse<any>> {
    try {
      const response = await axios.get<ApiResponse<any>>(
        `${this.baseUrl}/status/workflow`
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // ========================================
  // DOCUMENT PREVIEW
  // ========================================

  /**
   * Get list of all PDFs for an order
   */
  async getOrderPDFs(orderId: string): Promise<ApiResponse<{ standard: string[]; total: number }>> {
    try {
      const response = await axios.get<ApiResponse<{ standard: string[]; total: number }>>(
        `${this.ordersUrl}/${orderId}/pdfs`
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Generate document preview
   */
  async generateDocumentPreview(
    orderId: string,
    fileType: 'pdf' | 'delivery_note' | 'invoice' | 'extra_file',
    options: {
      format?: 'png' | 'jpeg' | 'webp';
      width?: number;
      height?: number;
      quality?: number;
      page?: number;
    } = {}
  ): Promise<ApiResponse<{ previewPath: string; documentInfo: any; fileType: string; options: any }>> {
    try {
      const response = await axios.post<ApiResponse<{ previewPath: string; documentInfo: any; fileType: string; options: any }>>(
        `${this.ordersUrl}/${orderId}/preview`,
        { fileType, options }
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get comprehensive file information for an order
   */
  async getOrderFilesInfo(orderId: string): Promise<ApiResponse<any>> {
    try {
      const response = await axios.get<ApiResponse<any>>(
        `${this.ordersUrl}/${orderId}/files/info`
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Download all files for an order as ZIP
   */
  async downloadOrderFilesZip(orderId: string): Promise<void> {
    try {
      const response = await axios.get(`${this.ordersUrl}/${orderId}/files/download/bulk`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `order_${orderId}_files.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Download files from multiple orders as ZIP
   */
  async downloadMultipleOrdersZip(
    orderIds: string[],
    fileTypes: string[] = ['pdf']
  ): Promise<void> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/orders/bulk/download`,
        { orderIds, fileTypes },
        { responseType: 'blob' }
      );
      
      const timestamp = new Date().toISOString().split('T')[0];
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders_bulk_${timestamp}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get status color for UI
   */
  static getStatusColor(status: string): string {
    switch (status) {
      case 'draft': return '#757575';      // Gray
      case 'ready': return '#1976d2';      // Blue
      case 'sent': return '#ed6c02';       // Orange
      case 'partial': return '#f57c00';    // Orange
      case 'delivered': return '#2e7d32';  // Green
      case 'completed': return '#388e3c';  // Dark Green
      default: return '#757575';
    }
  }

  /**
   * Get display name for order type
   */
  static getOrderTypeDisplayName(orderType: string): string {
    switch (orderType) {
      case 'material': return 'Material Order';
      case 'heat_treatment': return 'Heat Treatment';
      case 'coating': return 'Coating Order';
      default: return orderType;
    }
  }

  /**
   * Get display name for status
   */
  static getStatusDisplayName(status: string): string {
    switch (status) {
      case 'draft': return 'Draft';
      case 'ready': return 'Ready';
      case 'sent': return 'Sent';
      case 'partial': return 'Partial Delivery';
      case 'delivered': return 'Delivered';
      case 'completed': return 'Completed';
      default: return status;
    }
  }

  // ========================================
  // POSITION FILE OPERATIONS
  // ========================================

  /**
   * Upload files for a specific position
   */
  async uploadPositionFiles(
    orderId: string,
    positionId: string,
    files: File[],
    description?: string
  ): Promise<ApiResponse<any[]>> {
    try {
      console.log('🔍 [DEBUG] uploadPositionFiles called with:', {
        orderId,
        positionId,
        filesCount: files.length,
        files: files.map(f => ({ name: f.name, size: f.size, type: f.type })),
        description
      });

      const formData = new FormData();
      
      files.forEach(file => {
        formData.append('files', file);
      });
      
      if (description) {
        formData.append('description', description);
      }

      const uploadUrl = `${this.ordersUrl}/${orderId}/positions/${positionId}/files`;
      console.log('🔍 [DEBUG] Making upload request to:', uploadUrl);

      const response = await axios.post<ApiResponse<any[]>>(
        uploadUrl,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      console.log('🔍 [DEBUG] Upload response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('🔍 [DEBUG] Upload error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get all files for a specific position
   */
  async getPositionFiles(orderId: string, positionId: string): Promise<ApiResponse<any[]>> {
    try {
      const response = await axios.get<ApiResponse<any[]>>(
        `${this.ordersUrl}/${orderId}/positions/${positionId}/files`
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all position files for an order
   */
  async getOrderPositionFiles(orderId: string): Promise<ApiResponse<any>> {
    try {
      const response = await axios.get<ApiResponse<any>>(
        `${this.ordersUrl}/${orderId}/files/positions`
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete a position file
   */
  async deletePositionFile(
    orderId: string,
    positionId: string,
    fileId: string
  ): Promise<ApiResponse<void>> {
    try {
      const response = await axios.delete<ApiResponse<void>>(
        `${this.ordersUrl}/${orderId}/positions/${positionId}/files/${fileId}`
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Update position file description
   */
  async updatePositionFileDescription(
    orderId: string,
    positionId: string,
    fileId: string,
    description: string
  ): Promise<ApiResponse<void>> {
    try {
      const response = await axios.put<ApiResponse<void>>(
        `${this.ordersUrl}/${orderId}/positions/${positionId}/files/${fileId}`,
        { description }
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }
}

// Export singleton instance
export const unifiedOrderService = new UnifiedOrderService();
export { UnifiedOrderService };
